/**
 * SmartstoreProductSearch — 스마트스토어 상품 검색·가격 분석
 *
 * 네이버 커머스 API로 경쟁 상품을 상품명 키워드 검색하고,
 * 가격 분포 히스토그램 + 요약 통계 + 경쟁상품 기반 AI 가격 추천을 제공한다.
 * 상품 가격은 표시 전용(읽기) — 여기서 편집하지 않는다.
 */

import { useMemo, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import {
  Button,
  Input,
  Select,
  Card,
  StatCard,
  Table,
  Pagination,
  Drawer,
  Spinner,
  Alert,
  Badge,
  EmptyState,
  BarChart,
  type TableColumn,
  type BarDatum,
} from '@/ui'
import styles from './SmartstoreProductSearch.module.css'

interface ProductItem {
  channelProductNo: string
  name: string
  sellerManagementCode?: string
  salePrice: number
  discountedPrice: number
  discountRate: number
  stockQuantity: number
  statusType: string
  storeUrl: string
}

interface SearchResponse {
  items: ProductItem[]
  totalElements: number
  totalPages: number
  page: number
}

interface OptionDetail {
  optionName: string
  optionPrice: number
  stockQuantity: number
}

interface DetailResponse {
  name: string
  salePrice: number
  options: OptionDetail[]
}

const PAGE_SIZE = 50

/** 판매상태 라벨 매핑 */
const STATUS_LABEL: Record<string, string> = {
  SALE: '판매중',
  OUTOFSTOCK: '품절',
  SUSPENSION: '판매중지',
  CLOSE: '판매종료',
  PROHIBITION: '판매금지',
  UNKNOWN: '알수없음',
}

function statusColor(status: string): 'green' | 'red' | 'gray' {
  if (status === 'SALE') return 'green'
  if (status === 'OUTOFSTOCK' || status === 'SUSPENSION' || status === 'CLOSE') return 'red'
  return 'gray'
}

/** 가격 배열을 만원 단위 6구간 히스토그램 bin으로 집계 */
function buildHistogram(prices: number[]): BarDatum[] {
  const bins = [
    { label: '~1만', min: 0, max: 10000 },
    { label: '1~2만', min: 10000, max: 20000 },
    { label: '2~3만', min: 20000, max: 30000 },
    { label: '3~4만', min: 30000, max: 40000 },
    { label: '4~5만', min: 40000, max: 50000 },
    { label: '5만+', min: 50000, max: Infinity },
  ]
  return bins.map((b) => ({
    label: b.label,
    value: prices.filter((p) => p >= b.min && p < b.max).length,
  }))
}

/** 정렬된 배열의 중앙값 */
function median(sorted: number[]): number {
  if (sorted.length === 0) return 0
  return sorted[Math.floor(sorted.length / 2)]
}

export function SmartstoreProductSearch() {
  const { addToast } = useToast()

  // draft: 입력 중 / applied: 검색 버튼으로 확정된 값
  const [draftKeyword, setDraftKeyword] = useState('')
  const [draftStatus, setDraftStatus] = useState('')
  const [appliedKeyword, setAppliedKeyword] = useState('')
  const [appliedStatus, setAppliedStatus] = useState('')
  const [page, setPage] = useState(1)
  const [searched, setSearched] = useState(false)

  const [selected, setSelected] = useState<ProductItem | null>(null)

  const path = useMemo(() => {
    if (!searched) return null
    const p = new URLSearchParams()
    p.set('page', String(page))
    p.set('size', String(PAGE_SIZE))
    if (appliedKeyword.trim()) p.set('keyword', appliedKeyword.trim())
    if (appliedStatus) p.set('status', appliedStatus)
    return `/api/admin/smartstore/products?${p.toString()}`
  }, [searched, page, appliedKeyword, appliedStatus])

  const { data, isLoading, error } = useApiFetch<SearchResponse>(path)

  // 상세(옵션) — Drawer 열릴 때만 lazy 조회
  const detailPath = selected
    ? `/api/admin/smartstore/products/${encodeURIComponent(selected.channelProductNo)}`
    : null
  const { data: detail, isLoading: detailLoading, error: detailError } =
    useApiFetch<DetailResponse>(detailPath)

  // AI 가격 추천
  const insightMutation = useApiMutation<{ insights: string[] }>(
    '/api/admin/smartstore/products/price-insight',
  )

  const items = data?.items ?? []

  // 요약 통계 (할인가 기준)
  const stats = useMemo(() => {
    const prices = items.map((i) => i.discountedPrice).filter((n) => n > 0).sort((a, b) => a - b)
    const avgDiscount =
      items.length > 0
        ? items.reduce((acc, i) => acc + i.discountRate, 0) / items.length
        : 0
    return {
      count: data?.totalElements ?? items.length,
      median: median(prices),
      min: prices[0] ?? 0,
      max: prices[prices.length - 1] ?? 0,
      avgDiscount,
      histogram: buildHistogram(prices),
      hasData: prices.length > 0,
    }
  }, [items, data?.totalElements])

  const handleSearch = () => {
    setAppliedKeyword(draftKeyword)
    setAppliedStatus(draftStatus)
    setPage(1)
    setSearched(true)
    insightMutation.reset()
  }

  const handleAnalyze = async () => {
    if (items.length === 0) {
      addToast({ type: 'info', message: '분석할 검색 결과가 없습니다' })
      return
    }
    const res = await insightMutation.trigger({
      keyword: appliedKeyword,
      competitors: items.map((i) => ({
        name: i.name,
        salePrice: i.salePrice,
        discountedPrice: i.discountedPrice,
      })),
    })
    if (!res) {
      addToast({ type: 'error', message: insightMutation.error ?? 'AI 분석에 실패했습니다' })
    }
  }

  const columns: TableColumn<ProductItem>[] = [
    { key: 'name', label: '상품명', render: (r) => r.name },
    {
      key: 'salePrice',
      label: '정가',
      align: 'right',
      render: (r) =>
        r.discountedPrice < r.salePrice ? (
          <span className={styles.strike}>{r.salePrice.toLocaleString()}</span>
        ) : (
          <span>{r.salePrice.toLocaleString()}</span>
        ),
    },
    {
      key: 'discountedPrice',
      label: '할인가',
      align: 'right',
      render: (r) => <span className={styles.price}>{r.discountedPrice.toLocaleString()}</span>,
    },
    {
      key: 'discountRate',
      label: '할인율',
      align: 'right',
      render: (r) =>
        r.discountRate > 0 ? (
          <span className={styles.disc}>{Math.round(r.discountRate * 100)}%↓</span>
        ) : (
          <span className={styles.muted}>-</span>
        ),
    },
    { key: 'stockQuantity', label: '재고', align: 'right', render: (r) => r.stockQuantity.toLocaleString() },
    {
      key: 'statusType',
      label: '상태',
      render: (r) => (
        <Badge color={statusColor(r.statusType)}>{STATUS_LABEL[r.statusType] ?? r.statusType}</Badge>
      ),
    },
    {
      key: 'sellerManagementCode',
      label: '판매자코드',
      render: (r) => <span className={styles.code}>{r.sellerManagementCode ?? '-'}</span>,
    },
    {
      key: 'storeUrl',
      label: '링크',
      align: 'center',
      render: (r) => (
        <a
          href={r.storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.linkBtn}
          onClick={(e) => e.stopPropagation()}
          title="스마트스토어에서 열기"
        >
          <ExternalLink size={15} />
        </a>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>상품 가격 분석</h1>
        <p className={styles.subtitle}>
          네이버 스마트스토어 경쟁 상품의 실시간 가격을 검색하고, 가격 분포와 AI 추천으로 가격 전략을 참고합니다.
        </p>
      </div>

      {/* 검색 바 */}
      <Card>
        <div className={styles.searchbar}>
          <Input
            className={styles.searchInput}
            placeholder="상품명 키워드 (예: 청첩장)"
            value={draftKeyword}
            onChange={(e) => setDraftKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch()
            }}
          />
          <Select
            value={draftStatus}
            onChange={(e) => setDraftStatus(e.target.value)}
            options={[
              { value: '', label: '판매상태: 전체' },
              { value: 'SALE', label: '판매중' },
              { value: 'OUTOFSTOCK', label: '품절' },
              { value: 'SUSPENSION', label: '판매중지' },
            ]}
          />
          <Button onClick={handleSearch}>검색</Button>
        </div>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {searched && (
        <>
          {/* 히스토그램 + 통계 */}
          <div className={styles.grid2}>
            <Card title="가격 분포 (할인가 기준)">
              {isLoading ? (
                <div className={styles.center}><Spinner /></div>
              ) : stats.hasData ? (
                <BarChart data={stats.histogram} showTooltip formatTooltip={(d) => `${d.label}: ${d.value}개`} />
              ) : (
                <div className={styles.center}>데이터 없음</div>
              )}
            </Card>

            <Card title="요약 통계">
              <div className={styles.statGrid}>
                <StatCard label="검색 상품 수" value={`${stats.count.toLocaleString()}개`} />
                <StatCard label="판매가 중앙값" value={`${stats.median.toLocaleString()}원`} />
                <StatCard label="평균 할인율" value={`${Math.round(stats.avgDiscount * 100)}%`} />
                <StatCard
                  label="최저 / 최고"
                  value={`${stats.min.toLocaleString()} / ${stats.max.toLocaleString()}`}
                  sub="원"
                />
              </div>
            </Card>
          </div>

          {/* AI 가격 추천 */}
          <Card
            title="AI 가격 추천"
            extra={
              <Button
                size="sm"
                onClick={handleAnalyze}
                disabled={insightMutation.isLoading || items.length === 0}
              >
                {insightMutation.isLoading ? '분석 중…' : '분석하기'}
              </Button>
            }
          >
            {insightMutation.isLoading ? (
              <div className={styles.center}><Spinner /></div>
            ) : insightMutation.data?.insights?.length ? (
              <div className={styles.aiBox}>
                <p className={styles.aiRec}>{insightMutation.data.insights[0]}</p>
                {insightMutation.data.insights.length > 1 && (
                  <ul className={styles.aiList}>
                    {insightMutation.data.insights.slice(1).map((line, idx) => (
                      <li key={idx}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <p className={styles.aiHint}>검색된 경쟁상품 {items.length}개를 기준으로 권장 판매가를 분석합니다. "분석하기"를 눌러주세요.</p>
            )}
          </Card>

          {/* 검색 결과 테이블 */}
          <Card title="검색 결과">
            {isLoading ? (
              <div className={styles.center}><Spinner /></div>
            ) : items.length === 0 ? (
              <EmptyState title="검색 결과가 없습니다" description="다른 키워드로 검색해보세요." />
            ) : (
              <>
                <Table
                  columns={columns}
                  data={items}
                  rowKey={(r) => r.channelProductNo}
                  hoverable
                  onRowClick={(r) => setSelected(r)}
                />
                {(data?.totalPages ?? 1) > 1 && (
                  <div className={styles.pagination}>
                    <Pagination current={page} total={data?.totalPages ?? 1} onChange={setPage} />
                  </div>
                )}
              </>
            )}
          </Card>
        </>
      )}

      {/* 옵션 상세 Drawer */}
      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected?.name ?? '옵션 상세'}>
        {detailLoading ? (
          <div className={styles.center}><Spinner /></div>
        ) : detailError ? (
          <Alert variant="danger">{detailError}</Alert>
        ) : detail ? (
          <div>
            <p className={styles.drawerMeta}>정가 {detail.salePrice.toLocaleString()}원</p>
            {detail.options.length === 0 ? (
              <EmptyState title="옵션 없음" description="단일 상품입니다." />
            ) : (
              <div className={styles.optList}>
                {detail.options.map((o, idx) => (
                  <div key={idx} className={styles.optRow}>
                    <span className={styles.optName}>{o.optionName}</span>
                    <span className={styles.optPrice}>
                      {o.optionPrice >= 0 ? '+' : ''}
                      {o.optionPrice.toLocaleString()}원
                    </span>
                  </div>
                ))}
              </div>
            )}
            {selected && (
              <a href={selected.storeUrl} target="_blank" rel="noopener noreferrer" className={styles.drawerLink}>
                <ExternalLink size={15} /> 스마트스토어에서 열기
              </a>
            )}
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}
